// utils/pbKey.js

import { getPublicKey, cachePublicKey, getUswdSigningPublicKey,
  cacheUswdSigningPublicKey,
 } from "./Cryptoutils";

/**
 * Resolves a peer's public key with a three-tier lookup:
 *   1. In-memory cache  (fastest)
 *   2. localStorage     (persisted across sessions)
 *   3. Socket fetch     (network fallback)
 *
 * @param {string|number} chatId  - The peer's chat ID to look up
 * @param {object}        socket  - The active Socket.IO instance
 * @returns {Promise<object|null>}  The public key object, or null on failure
 */
export async function fetchAndCachePublicKey(chatId, socket) {
  if (!chatId || !socket) return null;

  const keyId = String(chatId);

  // =====================================================
  // 🔍 STEP 1: MEMORY CACHE (FASTEST)
  // =====================================================
  const memKey = getPublicKey(keyId);
  if (memKey) {
    return memKey;
  }

  // =====================================================
  // 🔍 STEP 2: LOCAL STORAGE (PERSISTED)
  // =====================================================
  try {
    const localRaw = localStorage.getItem(`e2ee_pubkey_${keyId}`);
    if (localRaw) {
      const parsed = JSON.parse(localRaw);
      cachePublicKey(keyId, parsed);
      return parsed;
    }
  } catch (err) {
    // failed to parse localStorage public key
  }

  // =====================================================
  // 🌐 STEP 3: SOCKET FETCH (FALLBACK)
  // =====================================================
  return new Promise((resolve) => {
    socket.emit("get-public-key", { peerChatId: keyId }, (res) => {
      if (res?.publicKey) {
        cachePublicKey(keyId, res.publicKey);

        try {
          localStorage.setItem(
            `e2ee_pubkey_${keyId}`,
            JSON.stringify(res.publicKey)
          );
        } catch (err) {
          // failed to save public key to localStorage
        }

        resolve(res.publicKey);
      } else {
        resolve(null);
      }
    });
  });
}

/* =========================================================
   🔐 USWD SIGNING PUBLIC KEY FETCHER
========================================================= */

/**
 * Resolves a user's USWD signing public key
 * with a three-tier lookup:
 *
 *   1. In-memory cache
 *   2. localStorage
 *   3. Socket fetch
 */
export async function fetchAndCacheUswdSigningPublicKey(
  chatId,
  socket
) {

  if (!chatId || !socket) {
    return null;
  }

  const keyId =
    String(chatId);

  /* =====================================================
     🔍 STEP 1: MEMORY CACHE
  ===================================================== */

  const memKey =
    getUswdSigningPublicKey(
      keyId
    );

  if (memKey) {
    return memKey;
  }

  /* =====================================================
     🔍 STEP 2: LOCAL STORAGE
  ===================================================== */

  try {

    const localRaw =
      localStorage.getItem(
        `signing_pubkey_${keyId}`
      );

    if (localRaw) {

      const parsed =
        JSON.parse(localRaw);

      cacheUswdSigningPublicKey(
        keyId,
        parsed
      );

      return parsed;

    }

  } catch (err) {
    // failed to parse localStorage signing public key
  }

  /* =====================================================
     🌐 STEP 3: SOCKET FETCH
  ===================================================== */

  return new Promise((resolve) => {

    socket.emit(
      "get-uswd-signing-public-key",
      {
        peerChatId: keyId,
      },
      (res) => {

        if (res?.publicKey) {

          cacheUswdSigningPublicKey(
            keyId,
            res.publicKey
          );

          try {

            localStorage.setItem(
              `signing_pubkey_${keyId}`,
              JSON.stringify(
                res.publicKey
              )
            );

          } catch (err) {
            // failed to save signing public key to localStorage
          }

          resolve(
            res.publicKey
          );

        } else {

          resolve(null);

        }

      }
    );

  });

}